import { useEffect, useState } from "react";

import { BsLink45Deg } from "react-icons/bs";
import classNames from "classnames";

import ReadMore from "../../components/common/ReadMore";
import TextNavBackground from "../../components/common/TextNavBackground";
import PlayerAvatar from "../../components/elements/players/PlayerAvatar";
import PlayerBioStats from "../../components/elements/players/PlayerBioStats";
import PlayerCardStyle from "../../components/elements/players/PlayerCardStyle";
import PlayerStats from "../../components/elements/players/PlayerStats";
import { apiWrapper } from "../../utils/api/apiWrapper";
import {
  kKotokoPlayerDetailURL,
  kKotokoPlayersLegendsListURL,
} from "../../utils/constants/apiUrls";
import { GOALKEEPER, GUEST_TOKEN } from "../../utils/constants/names";
import { ONE_MONTH_REFRESH_INTERVAL_SECONDS } from "../../utils/constants/numbers";
import { convertToShortMonth } from "../../utils/helpers/dateAndTime";
import {
  customSeoConfig,
  getPlayerGroup,
  getPlayerPosition,
} from "../../utils/helpers/helpers";
import SocialShareIcons from "../../components/common/icons/SocialShareIcons";
import CopyToClipboard from "react-copy-to-clipboard";
import WebShareIcon from "../../components/common/icons/WebShareIcon";
import NotifyToastContainer, {
  notify,
} from "../../components/common/NotifyToastContainer";

const LegendProfile = ({ player }) => {
  const playerPublicUrl = player.public_url;
  const [playerDateOfBirth, setPlayerDateOfBirth] = useState("");
  const [playerJoinedDate, setplayerJoinedDate] = useState("");
  const [value, setValue] = useState(playerPublicUrl);
  const [copied, setCopied] = useState(false);

  const playerGroup = getPlayerGroup(player);
  const playerPositionFormatted = getPlayerPosition(player.player_position);

  const stats = [
    { label: "Country", value: player.player_country_name },
    { label: "Date of birth", value: playerDateOfBirth },
    { label: "Joined", value: playerJoinedDate },
  ];

  const bioStat = [
    { label: "Age", value: player.player_age },
    { label: "Appearance", value: player.player_appearances },

    player.player_position === GOALKEEPER
      ? { label: "Clean sheets", value: player.player_clean_sheet }
      : { label: "Goals scored", value: player.player_goals_scored },
    { label: "Group", value: playerGroup },
  ];

  const playerFullName = `${player.player_first_name} ${player.player_last_name}`;
  const playerBioCaption = player.player_biography.slice(0, 120);
  const playerSeoTitle = `${playerFullName} | ${playerPositionFormatted}`;

  useEffect(() => {
    setPlayerDateOfBirth(convertToShortMonth(player.player_date_of_birth));
    setplayerJoinedDate(convertToShortMonth(player.player_joined_date));
  }, [player.player_date_of_birth, player.player_joined_date]);

  return (
    <>
      {customSeoConfig(
        playerSeoTitle,
        playerBioCaption,
        player.player_picture,
        playerPublicUrl,
        "website"
      )}

      <TextNavBackground title={playerFullName} />

      <div className="relative py-16 sm:py-10">
        <div className="lg:mx-auto lg:max-w-7xl lg:px-8 lg:grid lg:grid-cols-2 lg:gap-24 lg:items-start">
          <div className="relative sm:py-16 lg:py-0">
            <PlayerCardStyle />

            <PlayerAvatar
              playerAvatar={player.player_picture}
              playerFullName={playerFullName}
              playerPosition={player.player_position}
              playerStatus={player.player_status}
            />
          </div>

          <div className="relative mx-auto max-w-md px-6 sm:max-w-3xl lg:px-0">
            {/* Content area */}

            <div className="pt-12 sm:pt-16 lg:pt-20">
              <div className="flex justify-center items-center space-x-2 mb-4">
                <SocialShareIcons
                  title={playerFullName}
                  caption={playerBioCaption}
                  url={playerPublicUrl}
                />

                <CopyToClipboard
                  text={value}
                  onCopy={() => {
                    setCopied({ copied: true });
                    setValue(playerPublicUrl);
                  }}
                >
                  <BsLink45Deg
                    onClick={notify}
                    size={30}
                    className={classNames(
                      copied ? "text-green-600" : "",
                      "cursor-pointer text-gray-500 hover:text-black"
                    )}
                  />
                </CopyToClipboard>

                <WebShareIcon
                  title={playerFullName}
                  caption={playerBioCaption}
                  url={playerPublicUrl}
                />
              </div>

              <PlayerBioStats playerBioStats={bioStat} />

              <div className="mt-14 text-gray-500 space-y-6">
                <h1 className="text-base font-medium text-gray-500 border-b-2">
                  {"Biography"}
                </h1>

                <ReadMore>{player.player_biography}</ReadMore>
              </div>
            </div>

            <PlayerStats
              stats={stats}
              countryCode={player.player_country_code}
            />
          </div>
        </div>
      </div>
      {copied ? <NotifyToastContainer /> : null}
    </>
  );
};

export default LegendProfile;

export const getStaticProps = async (context) => {
  const token = process.env.GUSET_USER_TOKEN
    ? process.env.GUSET_USER_TOKEN
    : GUEST_TOKEN;

  const playerSlug = context.params.slug;

  const player = await apiWrapper.get(
    kKotokoPlayerDetailURL + `?slug=${playerSlug}`,
    token
  );

  return {
    props: {
      player: player?.data,
    },
    revalidate: ONE_MONTH_REFRESH_INTERVAL_SECONDS,
  };
};

export async function getStaticPaths() {
  const token = process.env.GUSET_USER_TOKEN
    ? process.env.GUSET_USER_TOKEN
    : GUEST_TOKEN;

  const players = await apiWrapper.get(kKotokoPlayersLegendsListURL, token);

  const data = players?.data;

  const kotokoLegends = [
    ...data.goalkeepers,
    ...data.defenders,
    ...data.midfielders,
    ...data.attackers,
  ];

  const paths = kotokoLegends.map((player) => ({
    params: { slug: player.slug },
  }));

  return { paths, fallback: "blocking" };
}
